/**
 * RecurrenceBuilderField (Plan 03-08).
 *
 * Form field that builds and displays a RecurrenceBuilder JSON object.
 * Per UI-SPEC §6 EventEditSheet "Gjentar seg":
 *   - Summary preview line (e.g., "Hver tirsdag · til 30.06.2026")
 *   - Inline type radio: Aldri / Hver dag / Hverdager / Ukentlig / Månedlig
 *   - Conditional sub-fields per type (D-03 pragmatic subset)
 *   - Optional "Slutter (valgfritt)" date field
 *
 * Output: validated RecurrenceBuilder JSON via onChange.
 * D-03: disallowed in v1: COUNT, BYSETPOS, BYYEARDAY, FREQ=YEARLY. Not exposed.
 *
 * Norwegian weekday names resolved via Intl.DateTimeFormat('nb-NO', {weekday:'long'}).
 *
 * Utseende (#157, steg 7b, Stell_Hendelse-skissen): sammendraget er skissens
 * «Gjentas»-rad — glassrad med «Gjentas» til venstre og valget + chevron til
 * høyre. Raden folder ut valgene under seg i arket (aria-expanded) i stedet
 * for å åpne en ny side, så arket forblir ett ark (D9). Valgene er `Chip`,
 * feltene bruker `field`. Logikken og byggerens form er uendret.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Icon } from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { RecurrenceBuilder } from '@stell/shared';
import type { RecurrenceBuilder as RecurrenceBuilderType } from '@stell/shared';

/** Map ISO weekday number (1=Mon..7=Sun) to Norwegian short label. */
const NB_WEEKDAY_SHORT = ['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'] as const;

/** Get long Norwegian weekday name for a given ISO weekday number. */
function nbWeekdayLong(iso: number): string {
  // Create a date that falls on the correct weekday
  // Monday = 1, use epoch week: 1970-01-05 is Monday
  const baseMonday = new Date(1970, 0, 5); // Monday Jan 5, 1970
  const d = new Date(baseMonday);
  d.setDate(baseMonday.getDate() + (iso - 1));
  return new Intl.DateTimeFormat('nb-NO', { weekday: 'long' }).format(d);
}

/** Build human-readable summary string for a RecurrenceBuilder. */
function buildSummary(
  b: RecurrenceBuilderType,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  let base = '';
  switch (b.type) {
    case 'once':
      base = t('calendar.recurrence.summary.once');
      break;
    case 'daily':
      if (b.interval && b.interval > 1) {
        base = t('calendar.recurrence.summary.dailyEvery', { n: b.interval });
      } else {
        base = t('calendar.recurrence.summary.daily');
      }
      break;
    case 'weekdays':
      base = t('calendar.recurrence.summary.weekdays');
      break;
    case 'weekly': {
      const name = nbWeekdayLong(b.weekday);
      if (b.interval && b.interval > 1) {
        base = t('calendar.recurrence.summary.weeklyEvery', { m: b.interval, weekdayName: name });
      } else {
        base = t('calendar.recurrence.summary.weekly', { weekdayName: name });
      }
      break;
    }
    case 'monthly':
      base = t('calendar.recurrence.summary.monthly', { n: b.dayOfMonth });
      break;
  }

  if (b.type !== 'once' && b.until) {
    const d = new Date(b.until);
    const dateStr = d.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
    base += t('calendar.recurrence.summary.untilSuffix', { date: dateStr });
  }

  return base;
}

type RecurrenceType = RecurrenceBuilderType['type'];

const TYPE_OPTIONS: RecurrenceType[] = ['once', 'daily', 'weekdays', 'weekly', 'monthly'];
const TYPE_KEYS: Record<RecurrenceType, string> = {
  once: 'calendar.recurrence.never',
  daily: 'calendar.recurrence.daily',
  weekdays: 'calendar.recurrence.weekdays',
  weekly: 'calendar.recurrence.weekly',
  monthly: 'calendar.recurrence.monthly',
};

export interface RecurrenceBuilderFieldProps {
  value: RecurrenceBuilderType;
  onChange: (next: RecurrenceBuilderType) => void;
}

export function RecurrenceBuilderField({ value, onChange }: RecurrenceBuilderFieldProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  /** Set the recurrence type; reset sub-fields to defaults. */
  const setType = (type: RecurrenceType) => {
    let next: RecurrenceBuilderType;
    switch (type) {
      case 'once':
        next = { type: 'once' };
        break;
      case 'daily':
        next = { type: 'daily' };
        break;
      case 'weekdays':
        next = { type: 'weekdays', days: [1, 2, 3, 4, 5] };
        break;
      case 'weekly':
        // Default to today's weekday (1=Mon..7=Sun)
        {
          const dow = new Date().getDay(); // 0=Sun..6=Sat
          const iso = dow === 0 ? 7 : dow;
          next = { type: 'weekly', weekday: iso };
        }
        break;
      case 'monthly':
        next = { type: 'monthly', dayOfMonth: new Date().getDate() };
        break;
    }
    // Preserve until if set
    const until = value.type !== 'once' ? value.until : undefined;
    if (type !== 'once' && until) {
      (next as { until?: string }).until = until;
    }
    const parsed = RecurrenceBuilder.safeParse(next);
    if (parsed.success) onChange(parsed.data);
  };

  /** Update until date. */
  const setUntil = (dateStr: string) => {
    if (!dateStr) {
      const { until: _removed, ...rest } = value as RecurrenceBuilderType & { until?: string };
      void _removed;
      const parsed = RecurrenceBuilder.safeParse(rest);
      if (parsed.success) onChange(parsed.data);
      return;
    }
    // Convert date-only input (YYYY-MM-DD) to ISO datetime
    const until = `${dateStr}T23:59:59.000Z`;
    const next = { ...value, until } as RecurrenceBuilderType;
    const parsed = RecurrenceBuilder.safeParse(next);
    if (parsed.success) onChange(parsed.data);
  };

  /** Update weekday multi-select (for 'weekdays' type). */
  const toggleWeekday = (iso: number) => {
    if (value.type !== 'weekdays') return;
    const days = value.days.includes(iso)
      ? value.days.filter((d) => d !== iso)
      : [...value.days, iso].sort((a, b) => a - b);
    if (days.length === 0) return; // at least one day required
    const parsed = RecurrenceBuilder.safeParse({ ...value, days });
    if (parsed.success) onChange(parsed.data);
  };

  const summary = buildSummary(value, (key, opts) =>
    opts ? t(key, opts) : t(key),
  );
  const untilValue =
    value.type !== 'once' && value.until
      ? value.until.slice(0, 10)
      : '';

  return (
    <div data-testid="recurrence-builder-field" className="flex flex-col gap-2">
      {/* «Gjentas»-raden: sammendrag + utfolding */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex h-14 w-full items-center justify-between gap-3 rounded-md border border-card-line bg-card px-4 text-left text-body font-bold text-ink"
        data-testid="recurrence-summary"
        aria-expanded={expanded}
      >
        <span className="shrink-0">{t('calendar.field.recurrence')}</span>
        <span className="flex min-w-0 items-center gap-1.5 text-ink-2">
          <span className="truncate">{summary}</span>
          <Icon.ChevR
            size={18}
            className={['shrink-0 transition-transform', expanded ? 'rotate-90' : ''].join(' ')}
          />
        </span>
      </button>

      {expanded && (
        <div className="flex flex-col gap-4 rounded-md border border-card-line bg-card p-4">
          {/* Type — ett valg */}
          <div
            role="group"
            aria-label={t('calendar.field.recurrence')}
            className="flex flex-wrap gap-2"
          >
            {TYPE_OPTIONS.map((type) => (
              <Chip
                key={type}
                selected={value.type === type}
                onClick={() => setType(type)}
                data-testid={`recurrence-type-${type}`}
              >
                {t(TYPE_KEYS[type])}
              </Chip>
            ))}
          </div>

          {/* Weekdays: multi-select day chips */}
          {value.type === 'weekdays' && (
            <div role="group" aria-labelledby="recurrence-days-label">
              <SectionLabel as="span" id="recurrence-days-label">
                {t('calendar.recurrence.onTheseDays')}
              </SectionLabel>
              <div className="flex flex-wrap gap-2">
                {NB_WEEKDAY_SHORT.map((label, idx) => {
                  const iso = idx + 1;
                  return (
                    <Chip
                      key={iso}
                      selected={value.days.includes(iso)}
                      onClick={() => toggleWeekday(iso)}
                      data-testid={`weekday-${iso}`}
                    >
                      {label}
                    </Chip>
                  );
                })}
              </div>
            </div>
          )}

          {/* Weekly: show interval option */}
          {value.type === 'weekly' && (
            <div className="flex flex-col">
              <SectionLabel as="label" htmlFor="recurrence-interval">
                {t('calendar.recurrence.interval')}
              </SectionLabel>
              <div className="w-24">
                <select
                  id="recurrence-interval"
                  value={value.interval ?? 1}
                  onChange={(e) => {
                    const interval = Number(e.target.value);
                    const next = { ...value, ...(interval > 1 ? { interval } : {}) };
                    if (interval === 1) {
                      const { interval: _rm, ...rest } = next as typeof value & { interval?: number };
                      void _rm;
                      const parsed = RecurrenceBuilder.safeParse(rest);
                      if (parsed.success) onChange(parsed.data);
                    } else {
                      const parsed = RecurrenceBuilder.safeParse(next);
                      if (parsed.success) onChange(parsed.data);
                    }
                  }}
                  className="field"
                  data-testid="weekly-interval"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Monthly: day of month display */}
          {value.type === 'monthly' && (
            <p className="m-0 text-body-s text-ink-2">
              {t('calendar.recurrence.dayOfMonth', { n: value.dayOfMonth })}
            </p>
          )}

          {/* Until date (all recurring types) */}
          {value.type !== 'once' && (
            <div className="flex flex-col">
              <SectionLabel as="label" htmlFor="recurrence-until">
                {t('calendar.recurrence.until')}
              </SectionLabel>
              <input
                id="recurrence-until"
                type="date"
                value={untilValue}
                onChange={(e) => setUntil(e.target.value)}
                className="field"
                data-testid="recurrence-until"
                lang="nb"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
